import { Helmet } from 'react-helmet-async';

const defaultSEO = {
  siteName: 'SurveysMaps.com',
  titleTemplate: '%s | SurveysMaps - CAD Drafting for Land Surveyors',
  defaultTitle: 'SurveysMaps - Professional CAD Drafting for Land Surveying',
  defaultDescription: 'Fast, accurate CAD drafting services for land surveyors. ALTA surveys, boundary surveys, topographic surveys, and more. 24-hour turnaround. Serving all 50 states.',
  siteUrl: 'https://surveysmaps.com',
  defaultImage: 'https://surveysmaps.com/og-image.jpg',
  twitterHandle: '@surveysmaps',
};

const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": "https://surveysmaps.com/#organization",
  "name": "SurveysMaps",
  "url": "https://surveysmaps.com/",
  "logo": "https://surveysmaps.com/logo192.png",
  "image": defaultSEO.defaultImage,
  "telephone": "+1-938-307-1829",
  "email": "enquiries@surveysmaps.com",
  "priceRange": "$$",
  "openingHours": "Mo-Sa 00:00-23:59",
  "contactPoint": [{ "@type": "ContactPoint", "telephone": "+1-938-307-1829", "contactType": "sales", "areaServed": ["US", "CA", "GB", "AU"], "availableLanguage": ["English", "French"] }],
  "areaServed": [
    { "@type": "Country", "name": "United States" }, { "@type": "Country", "name": "Canada" },
    { "@type": "Country", "name": "United Kingdom" }, { "@type": "Country", "name": "Australia" },
  ],
  "knowsAbout": ["Survey CAD drafting", "ALTA/NSPS land title survey drafting", "Boundary survey drafting", "Topographic survey drafting", "Civil 3D survey drafting", "GIS mapping", "Point cloud to CAD", "Survey data conversion", "As-built survey drafting", "Subdivision plat drafting"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog", "name": "Survey CAD Drafting Services",
    "itemListElement": ["ALTA/NSPS Survey Drafting", "Boundary Survey Drafting", "Topographic Survey Drafting", "Civil 3D Survey Drafting", "GIS Mapping Services", "LiDAR & Point Cloud to CAD", "Survey Data Conversion", "As-Built Survey Drafting"]
      .map(n => ({ "@type": "Offer", "itemOffered": { "@type": "Service", "name": n } })),
  },
};

// SEO: global entity graph — Organization/LocalBusiness, WebSite, SiteNavigationElement
const ADDRESS = { "@type": "PostalAddress", "streetAddress": "Sanskruti Apartment, Basement, 11th Ln, Laxmi Nagar", "addressLocality": "Jaysingpur", "addressRegion": "Maharashtra", "postalCode": "416101", "addressCountry": "IN" };
ORG_SCHEMA["@type"] = ["ProfessionalService", "LocalBusiness"];
ORG_SCHEMA.address = ADDRESS;
ORG_SCHEMA.sameAs = ["https://wa.me/19383071829"];

const ORGANIZATION_SCHEMA = {
  "@context": "https://schema.org", "@type": "Organization", "@id": "https://surveysmaps.com/#org",
  "name": "SurveysMaps", "url": "https://surveysmaps.com/", "logo": "https://surveysmaps.com/logo192.png", "address": ADDRESS,
  "contactPoint": ORG_SCHEMA.contactPoint, "sameAs": ORG_SCHEMA.sameAs,
};

const WEBSITE_SCHEMA = {
  "@context": "https://schema.org", "@type": "WebSite", "@id": "https://surveysmaps.com/#website",
  "name": "SurveysMaps", "url": "https://surveysmaps.com/", "publisher": { "@id": "https://surveysmaps.com/#org" }, "inLanguage": ["en-US", "en-CA", "en-GB", "en-AU", "fr-CA"],
};

const NAV_SCHEMA = {
  "@context": "https://schema.org", "@type": "ItemList", "name": "Main navigation",
  "itemListElement": [["Services", "/services"], ["Survey CAD Outsourcing", "/survey-cad-outsourcing"], ["Service Areas", "/service-areas"], ["USA", "/usa"], ["Canada", "/canada"], ["UK", "/uk"], ["Australia", "/australia"], ["Pricing", "/pricing"], ["Portfolio", "/portfolio"], ["Get a Quote", "/quote"], ["Contact", "/contact"]]
    .map(([name, path], i) => ({ "@type": "SiteNavigationElement", "position": i + 1, "name": name, "url": `https://surveysmaps.com${path}` })),
};

// SEO: enforce Google title (50–60 chars) and meta description (120–155 chars) lengths
const BRAND = ' | SurveysMaps';
export const fitTitle = (t) => {
  let parts = t.split(' | ').filter(p => p !== 'SurveysMaps');
  let out = parts.join(' | ') + BRAND;
  while (out.length > 60 && parts.length > 1) { parts.splice(parts.length - 1, 1); out = parts.join(' | ') + BRAND; }
  if (out.length > 60) out = `${parts[0].slice(0, 60 - BRAND.length - 1).replace(/\s+\S*$/, '').replace(/(\s+(for|in|of|and|to|the|a|an|&|with|on|at|by|vs|[-–]))+$/i, '').replace(/[\s:;,–-]+$/, '')}${BRAND}`;
  out = out.replace(/[\s\-–|:;,]+\| SurveysMaps$/, BRAND);
  if (out.length < 50) {
    const base = out.slice(0, -BRAND.length);
    const tag = [' – Survey CAD Drafting for Land Surveyors', ' – Outsourced Survey CAD Drafting', ' – Survey CAD Drafting Services', ' – Survey CAD Drafting', ' – CAD Drafting', ' – Outsourced', ' – Experts', ' – Pro']
      .find(t => (base + t + BRAND).length <= 60 && (base + t + BRAND).length >= 50 && !base.toLowerCase().includes(t.slice(3).toLowerCase())) || '';
    out = base + tag + BRAND;
  }
  return out;
};
export const fitDescription = (d) => {
  let out = d.replace(/\s+/g, ' ').trim();
  for (const add of [' Get a free quote within 24 hours.', ' 24–48 hr turnaround.', ' Trusted by US, Canadian, UK & Australian firms.']) {
    if (out.length >= 120) break;
    if ((out + add).length <= 155) out += add;
  }
  if (out.length > 155) {
    const cut = out.slice(0, 155);
    const end = Math.max(cut.lastIndexOf('. '), cut.endsWith('.') ? 154 : -1);
    out = end >= 100 ? cut.slice(0, end + 1) : `${out.slice(0, 154).replace(/[\s,;:–-]+\S*$/, '').replace(/[\s,;:–-]+$/, '')}.`;
  }
  return out;
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((it, i) => ({ "@type": "ListItem", "position": i + 1, "name": it.name, "item": `${defaultSEO.siteUrl}${it.path}` })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
});

export const serviceSchema = ({ name, description, path, area }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "description": description,
  "url": `${defaultSEO.siteUrl}${path}`,
  "provider": { "@id": "https://surveysmaps.com/#organization" },
  "serviceType": "Survey CAD Drafting",
  ...(area ? { "areaServed": area } : {}),
});

export function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noindex = false,
  lang = 'en',
  geoRegion = 'US',
  geoPlace = 'United States',
  alternates = [],
  jsonLd = [],
  preloadImage,
}) {
  const seoTitle = fitTitle(title || defaultSEO.defaultTitle);
  const seoDescription = fitDescription(description || defaultSEO.defaultDescription);
  const seoImage = image || defaultSEO.defaultImage;
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const seoUrl = url || `${defaultSEO.siteUrl}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
  const schemas = [ORG_SCHEMA, ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, NAV_SCHEMA, ...(Array.isArray(jsonLd) ? jsonLd : [jsonLd])];

  const defaultKeywords = 'CAD drafting, land surveying, ALTA survey, boundary survey, topographic survey, surveying services, CAD services, survey drafting, land survey drafting, professional surveying';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={keywords ? `${keywords}, ${defaultKeywords}` : defaultKeywords} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'} />
      <link rel="canonical" href={seoUrl} />
      {preloadImage && <link rel="preload" as="image" href={preloadImage} fetchpriority="high" />}
      {alternates.map(a => <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={`${defaultSEO.siteUrl}${a.path}`} />)}

      <meta property="og:type" content={type} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={lang.replace('-', '_')} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />

      <meta name="author" content="SurveysMaps.com" />
      <meta name="geo.region" content={geoRegion} />
      <meta name="geo.placename" content={geoPlace} />

      {schemas.map((sc, i) => <script key={i} type="application/ld+json">{JSON.stringify(sc)}</script>)}
    </Helmet>
  );
}

// SEO configurations for each page
export const pageSEO = {
  home: {
    title: 'Land Survey CAD Drafting Services Provider USA',
    description: 'Land survey CAD drafting services for US surveying firms. Outsource ALTA, boundary and topographic drafting to a dedicated provider. 24–48 hr turnaround.',
    keywords: 'land survey CAD drafting services, CAD drafting service provider in USA for land surveying, outsourcing company for land survey CAD drafting services, survey drafting services USA'
  },
  about: {
    title: 'About Us - Your Trusted CAD Drafting Partner',
    description: 'SurveysMaps is a specialized CAD drafting service provider focused exclusively on land surveying professionals. 10+ years experience, 500+ projects completed.',
    keywords: 'surveying company, CAD drafting team, land survey experts'
  },
  services: {
    title: 'CAD Drafting Services for All Survey Types',
    description: 'Complete CAD drafting solutions for 16+ survey types including ALTA, boundary, topographic, subdivision, construction, and more. Professional quality, fast delivery.',
    keywords: 'survey drafting services, CAD services, land survey types'
  },
  pricing: {
    title: 'Transparent Pricing for CAD Drafting Services',
    description: 'Flexible pricing options for CAD drafting: per project, monthly retainer, or enterprise plans. No hidden fees. Get a free quote today.',
    keywords: 'CAD drafting prices, survey drafting cost, affordable CAD services'
  },
  portfolio: {
    title: 'Our Work - CAD Drafting Project Portfolio',
    description: 'Browse our portfolio of completed CAD drafting projects across all survey types. ALTA surveys, boundary surveys, topographic surveys and more.',
    keywords: 'CAD drafting examples, survey portfolio, project samples'
  },
  contact: {
    title: 'Contact Us - Get a Free Quote',
    description: 'Contact SurveysMaps for a free, no-obligation quote on your CAD drafting project. We respond within 24 hours.',
    keywords: 'contact surveyors, CAD drafting quote, surveying consultation'
  },
  faq: {
    title: 'Frequently Asked Questions',
    description: 'Find answers to common questions about our CAD drafting services, turnaround times, file formats, pricing, and more.',
    keywords: 'CAD drafting FAQ, surveying questions, survey drafting help'
  },
  getStarted: {
    title: 'Get Started - Upload Your Survey Files',
    description: 'Start your CAD drafting project today. Upload your field data, sketches, and CAD files. Get a quote within 24 hours.',
    keywords: 'start survey project, upload CAD files, begin drafting'
  }
};

// Service-specific SEO
export const serviceSEO = {
  'alta-nsps': {
    title: 'ALTA/NSPS Land Title Survey Drafting',
    description: 'Professional ALTA/NSPS survey drafting services meeting all Table A requirements. Essential for commercial real estate transactions, title insurance, and due diligence.',
    keywords: 'ALTA survey drafting, NSPS survey, land title survey, commercial survey'
  },
  'topographic': {
    title: 'Topographic Survey Drafting Services',
    description: 'Detailed topographic survey drafting with contour generation, spot elevations, and surface features mapping. Essential for site development and engineering.',
    keywords: 'topographic survey drafting, contour mapping, elevation survey, site survey'
  },
  'boundary': {
    title: 'Boundary Survey Drafting Services',
    description: 'Accurate boundary survey drafting with property lines, bearings, distances, and legal descriptions. Perfect for property ownership and dispute resolution.',
    keywords: 'boundary survey drafting, property survey, land boundary, property lines'
  },
  'utility': {
    title: 'Utility Survey Drafting Services',
    description: 'Underground and overhead utility mapping and drafting services. Essential for construction planning and avoiding utility conflicts.',
    keywords: 'utility survey drafting, underground utilities, utility mapping'
  },
  'right-of-way': {
    title: 'Right-of-Way & Route Survey Drafting',
    description: 'Right-of-way and route survey drafting for roads, railways, pipelines, and transmission lines. Corridor surveys and easement documentation.',
    keywords: 'right of way survey, route survey, corridor survey, easement survey'
  },
  'as-built': {
    title: 'As-Built Survey Drafting Services',
    description: 'Post-construction as-built survey drafting documenting actual conditions. Essential for project closeout and facility management.',
    keywords: 'as-built survey drafting, construction survey, record drawings'
  },
  'record-of-survey': {
    title: 'Record of Survey (ROS) Drafting',
    description: 'Official Record of Survey drafting for boundary establishment, monument recovery, and county recording requirements.',
    keywords: 'record of survey, ROS drafting, corner record, monument survey'
  },
  'plot-plans': {
    title: 'Plot Plan Drafting Services',
    description: 'Detailed plot plan drafting for building permits, zoning compliance, and construction planning. Site improvements and setback verification.',
    keywords: 'plot plan drafting, site plan, building permit survey'
  },
  'final-plats': {
    title: 'Final Plat & Subdivision Plat Drafting',
    description: 'Subdivision plat and final plat drafting for property division and recording. Lot layouts, street dedications, and easement designations.',
    keywords: 'subdivision plat drafting, final plat, lot survey, property division'
  },
  'metes-bounds': {
    title: 'Metes & Bounds Survey Drafting',
    description: 'Traditional metes and bounds survey drafting using bearings, distances, and monument calls. Ideal for rural properties and irregular parcels.',
    keywords: 'metes and bounds survey, legal description, property description'
  },
  'hydrographic': {
    title: 'Hydrographic Survey Drafting Services',
    description: 'Underwater bathymetric and hydrographic survey drafting for marine construction, dredging, and waterway management.',
    keywords: 'hydrographic survey drafting, bathymetric mapping, underwater survey'
  },
  'construction-staking': {
    title: 'Construction Staking Plans & Layout',
    description: 'Construction staking plan drafting for buildings, roads, and utilities. Precise field layout from design plans.',
    keywords: 'construction staking, layout plans, stakeout survey'
  },
  'elevation-certificate': {
    title: 'FEMA Elevation Certificate Services',
    description: 'FEMA elevation certificate drafting for flood insurance and floodplain compliance. Lowest floor elevations and base flood comparisons.',
    keywords: 'elevation certificate, FEMA survey, flood insurance survey'
  },
  'site-plan': {
    title: 'Site Plan Survey Drafting',
    description: 'Comprehensive site plan survey drafting combining boundary, topographic, and improvement information for development planning.',
    keywords: 'site plan survey, development survey, property mapping'
  },
  'control-survey': {
    title: 'Control Survey Drafting Services',
    description: 'Control survey drafting establishing precise reference points for large-scale construction and infrastructure projects.',
    keywords: 'control survey, GPS survey, benchmark survey, geodetic survey'
  },
  'mortgage-survey': {
    title: 'Mortgage Survey Drafting Services',
    description: 'Residential mortgage survey drafting for home purchases and refinancing. Property verification and basic boundary identification.',
    keywords: 'mortgage survey, residential survey, home survey, loan survey'
  }
};
